import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { Item } from "@/types/item";
import type { z } from "zod";
import type { invoiceItemSchema } from "@/schema/schema";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const SearchModal = ({
  isOpen,
  onClose,
  onSelectItem,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: z.infer<typeof invoiceItemSchema>) => void;
}) => {
  const { format: formatDecimal } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["items", searchTerm],
    queryFn: async () => {
      const response = await api.get<Item[]>("/items", {
        params: {
          search: searchTerm,
        },
      });
      return response.data;
    },
    select: (data) =>
      data.filter(
        (item) =>
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.classification.includes(searchTerm) ||
          item.itemCode.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    enabled: isOpen,
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search Items</DialogTitle>
          <DialogDescription>
            Search for items by item code, description, or classification code.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by item code, description, or classification..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
              aria-label="Search items"
            />
          </div>
          <div className="relative border rounded-md">
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableHeader className="bg-white dark:bg-gray-950 sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="w-[150px]">Item Code</TableHead>
                    <TableHead className="w-[150px]">Classification</TableHead>
                    <TableHead className="w-[200px]">Description</TableHead>
                    <TableHead className="w-[150px]">Unit Price</TableHead>
                    <TableHead className="w-[100px]">Tax Rate</TableHead>
                    <TableHead className="w-[100px]">Discount Rate</TableHead>
                    <TableHead className="w-[100px]">Fee/Charge Rate</TableHead>
                    <TableHead className="w-[100px]">
                      Current Quantity
                    </TableHead>
                    <TableHead className="w-[100px]">Stock Type</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="text-center text-red-500"
                      >
                        Error loading items
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center">
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.id} className="hover:bg-muted">
                        <TableCell className="w-[150px]">
                          {item.itemCode}
                        </TableCell>
                        <TableCell className="w-[150px]">
                          {item.classification}
                        </TableCell>
                        <TableCell className="w-[200px]">
                          {item.description}
                        </TableCell>
                        <TableCell className="w-[150px]">
                          RM {formatDecimal(Number(item.unitPrice))}
                        </TableCell>
                        <TableCell className="w-[100px] text-center">
                          {item.taxRate}%
                        </TableCell>
                        <TableCell className="w-[100px] text-center">
                          {item.discountRate}%
                        </TableCell>
                        <TableCell className="w-[100px] text-center">
                          {item.feeChargeRate}%
                        </TableCell>
                        <TableCell className="w-[100px] text-center">
                          {item.quantity || 0}
                        </TableCell>
                        <TableCell className="w-[100px] text-center">
                          {item.stockType === "stock" ? "Stock" : "Non-Stock"}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => {
                              onSelectItem({
                                id: item.id,
                                classification: item.classification,
                                description: item.description,
                                quantity: 1, //default to 1
                                unitPrice: Number(item.unitPrice),
                                taxRate: Number(item.taxRate),
                                taxType: item.taxType as
                                  | "01"
                                  | "02"
                                  | "03"
                                  | "04"
                                  | "05"
                                  | "06"
                                  | "E",
                                taxAmount:
                                  item.isEditable === 1
                                    ? Number(item.taxAmount)
                                    : Number(0),
                                detailsOfTaxExemption:
                                  item.detailsOfTaxExemption,
                                amountExemptedFromTax:
                                  item.amountExemptedFromTax,
                                subtotal: Number(0), // calculate at document.tsx
                                totalExcludingTax: Number(0), // calculate at document.tsx
                                measurement: item.measurement,
                                discountRate: item.discountRate,
                                discountAmount: 0,
                                feeRate: item.feeChargeRate,
                                feeAmount: 0,
                                productTariffCode: item.productTariffCode,
                                countryOfOrigin: item.countryOfOrigin,
                                codeNumber: item.itemCode,
                                total: Number(0),
                                stockType: item.stockType as
                                  | "stock"
                                  | "non-stock",
                                availableQuantity: item.quantity || 0,
                                ...(item.isEditable !== undefined && { isEditable: item.isEditable }),
                              });
                              onClose();
                            }}
                            aria-label={`Select ${item.description}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
